import { Button, FileUpload, Modal, ModalContent, type UploadItem } from '@olytic/foundation';
import { useState } from 'react';

import { authenticatedFetch } from '../auth/authClient';
import type { ObjectKey } from '../navigation';
import { ATTACH_ACCEPT } from '../work/WorkFiles';
import { type FileEntity, uploadFile } from '../work/workApi';

/** The record pages whose Documents list takes uploads, and the document_links entity each one writes. */
export const FILE_ENTITIES: Partial<Record<ObjectKey, FileEntity>> = {
  opportunities: 'opportunity',
  accounts: 'account',
  meetings: 'meeting',
};

/** Upload files to a record's Documents list: Work's upload, with Foundation's FileUpload drop zone. */
export function RecordDocumentUpload({
  entity, recordId, recordName, onUploaded, onClose,
}: {
  entity: FileEntity;
  recordId: string;
  recordName: string;
  /** After each file is stored, so the Documents list reads again. */
  onUploaded: () => void;
  onClose: () => void;
}): JSX.Element {
  const [items, setItems] = useState<UploadItem[]>([]);
  const busy = items.some((i) => i.state === 'uploading' || i.state === 'queued');

  const add = async (list: File[]) => {
    const start = Date.now();
    const queued: UploadItem[] = list.map((f, i) => ({ id: `up-${start}-${i}`, name: f.name, size: f.size, state: 'queued' }));
    setItems((current) => [...queued, ...current]);
    const set = (itemId: string, next: Partial<UploadItem>) =>
      setItems((current) => current.map((i) => (i.id === itemId ? { ...i, ...next } : i)));
    for (const [i, f] of list.entries()) {
      const itemId = queued[i]!.id;
      set(itemId, { state: 'uploading' });
      try {
        await uploadFile(authenticatedFetch, entity, recordId, f);
        set(itemId, { state: 'ready' });
        onUploaded();
      } catch (e) {
        set(itemId, { state: 'failed', error: e instanceof Error ? e.message : `Could not upload ${f.name}.` });
      }
    }
  };

  return (
    <Modal open onOpenChange={(open) => (open || busy ? undefined : onClose())}>
      <ModalContent
        size="md"
        title="Upload files"
        description={`Added to ${recordName}'s documents.`}
        footer={<Button onClick={onClose} disabled={busy}>Done</Button>}
      >
        <FileUpload
          accept={ATTACH_ACCEPT}
          maxSizeMb={15}
          label="Drop files here, or choose them"
          hint="PDF, Word, Excel, PowerPoint, HTML, images, CSV or text, up to 15 MB each."
          items={items}
          onAdd={(list) => void add(list)}
        />
      </ModalContent>
    </Modal>
  );
}
